import { supabase } from '../lib/supabaseClient';

/* The RPC hasn't been created yet if customer_accounts.sql wasn't run. */
const isMissingFunction = (error) =>
  !!error &&
  (error.code === '42883' ||
    error.code === 'PGRST202' ||
    /could not find the function/i.test(error.message || '') ||
    /function .* does not exist/i.test(error.message || ''));

/**
 * Reduce stock for one product.
 *
 * Prefers the decrement_stock() database function: it reads and writes in a
 * single statement, so two people buying the last unit at the same moment
 * can't both succeed.
 *
 * Falls back to a read-then-write from the browser when that function is
 * missing. That keeps inventory moving on a database where the migration
 * hasn't been applied, but it IS racy under simultaneous orders — run
 * backend/sql/customer_accounts.sql to get the safe path.
 */
export const decrementStock = async (productId, qty) => {
  const { error } = await supabase.rpc('decrement_stock', {
    p_product_id: productId,
    p_qty: qty,
  });

  if (!error) return { ok: true, viaRpc: true };

  if (!isMissingFunction(error)) {
    console.error('Stock update error:', error);
    return { ok: false, error };
  }

  console.warn(
    'decrement_stock() not found — falling back to a client-side update. ' +
      'Run backend/sql/customer_accounts.sql for atomic stock handling.'
  );

  const { data: row, error: readError } = await supabase
    .from('products')
    .select('stock_quantity, purchase_count')
    .eq('id', productId)
    .maybeSingle();

  if (readError || !row) {
    console.error('Stock read failed:', readError);
    return { ok: false, error: readError };
  }

  const next = Math.max(0, Number(row.stock_quantity || 0) - qty);

  const { error: writeError } = await supabase
    .from('products')
    .update({
      stock_quantity: next,
      in_stock: next > 0,
      purchase_count: Number(row.purchase_count || 0) + 1,
    })
    .eq('id', productId);

  if (writeError) {
    console.error('Stock write failed:', writeError);
    return { ok: false, error: writeError };
  }

  return { ok: true, viaRpc: false };
};

/** Put units back when an order is cancelled. */
export const restoreStock = async (productId, qty) => {
  const { data: row, error: readError } = await supabase
    .from('products')
    .select('stock_quantity')
    .eq('id', productId)
    .maybeSingle();

  if (readError || !row) return { ok: false, error: readError };

  const next = Number(row.stock_quantity || 0) + qty;

  const { error: writeError } = await supabase
    .from('products')
    .update({ stock_quantity: next, in_stock: next > 0 })
    .eq('id', productId);

  return { ok: !writeError, error: writeError };
};


export const createOrder = async (orderData) => {
  try {
    const {
      customerName,
      customerPhone,
      customerEmail = null,
      products, // Array of { product, quantity }
      shippingAddress,
      couponCode = null,
      discountAmount: previewDiscount = 0,
    } = orderData;

    // Attach the order to the signed-in customer so it shows up in
    // their account history. Guest checkout leaves this null.
    const {
      data: { user },
    } = await supabase.auth.getUser();

    // Calculate totals
    const totalAmount = products.reduce(
      (sum, item) => sum + (item.product.price * item.quantity),
      0
    );

    const shippingCharges = 0; // Free shipping or calculate based on location

    /* These two are what the customer was SHOWN, not what the shop
       honours. This runs with the public anon key, so both are just a
       claim: the order_coupon_trg trigger (backend/sql/coupons.sql)
       re-prices the row from the coupon as it stands when the insert
       lands, and overwrites them. */
    const discountAmount = Math.max(0, Math.min(Number(previewDiscount) || 0, totalAmount));
    const finalAmount = totalAmount + shippingCharges - discountAmount;

    // Generate order number
    const orderNumber = `CS-${Date.now()}`;

    /* The id is generated HERE rather than read back from the insert.
       A guest has no auth.uid(), so the "orders read own" RLS policy
       cannot match the row they just created — .select() after the
       insert would come back empty and the order would look like it
       failed. Knowing the id up front means anon needs INSERT on
       orders and nothing else, so the table stays unreadable.
       See backend/sql/patch_3_restore_rls.sql. */
    const orderId =
      window.crypto?.randomUUID?.() ||
      // Older Safari / non-secure origins have no randomUUID.
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

    // 1. Create order
    const orderRow = {
      id: orderId,
      order_number: orderNumber,
      user_id: user?.id || null,
      customer_name: customerName,
      customer_phone: customerPhone,
      customer_email: customerEmail,
      total_amount: totalAmount,
      discount_amount: discountAmount,
      coupon_code: couponCode,
      shipping_charges: shippingCharges,
      final_amount: finalAmount,
      order_status: 'pending',
      payment_status: 'pending',
      shipping_address: shippingAddress,
      source: 'website',
    };

    /* Columns added by a migration that may not have been run yet:
       user_id comes from customer_accounts.sql, coupon_code from
       coupons.sql. If one is missing the insert fails naming it — drop
       that field and retry rather than losing the order entirely. */
    const OPTIONAL_COLUMNS = [
      ['user_id', 'backend/sql/customer_accounts.sql'],
      ['coupon_code', 'backend/sql/coupons.sql'],
    ];

    let payload = orderRow;
    let { error: orderError } = await supabase.from('orders').insert(payload);

    for (const [column, file] of OPTIONAL_COLUMNS) {
      if (!orderError) break;
      if (!new RegExp(`\\b${column}\\b`).test(orderError.message || '')) continue;

      console.warn(`orders.${column} is missing — run ${file}.`);

      const { [column]: dropped, ...rest } = payload;
      payload = rest;
      ({ error: orderError } = await supabase.from('orders').insert(payload));
    }

    if (orderError) throw orderError;

    // Everything downstream only needs these two fields.
    const order = { id: orderId, order_number: orderNumber };

    // 2. Create order items
    const orderItems = products.map(item => ({
      order_id: order.id,
      // Shop maps Supabase's uuid to `supabaseId` and product_code to `id`,
      // so prefer the uuid — product_id is a uuid FK.
      product_id: item.product.supabaseId || null,
      product_name: item.product.name,
      product_code: item.product.product_code || item.product.id,
      planet: item.product.planet,
      unit_price: item.product.price,
      quantity: item.quantity,
      total_price: item.product.price * item.quantity,
    }));

    const { error: itemsError } = await supabase
      .from('order_items')
      .insert(orderItems);

    if (itemsError) throw itemsError;

    // 3. Stock is reduced by the order_item_stock_trg trigger on
    //    order_items. It deliberately is NOT done from the browser:
    //    exposing a "decrement any product by N" function to the public
    //    key would let anyone zero the whole catalogue.

    return { success: true, order, orderNumber };
  } catch (error) {
    console.error('❌ Error creating order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Log WhatsApp message
 */
export const logWhatsAppMessage = async (messageData) => {
  try {
    const { orderId, phone, messageContent, messageType = 'order_inquiry' } = messageData;

    const { error } = await supabase
      .from('whatsapp_messages')
      .insert({
        order_id: orderId,
        phone_number: phone,
        message_content: messageContent,
        message_type: messageType,
        sent_successfully: true,
      });

    if (error) throw error;

    // Update order to mark WhatsApp sent
    await supabase
      .from('orders')
      .update({
        whatsapp_sent: true,
        whatsapp_sent_at: new Date().toISOString(),
      })
      .eq('id', orderId);

    return { success: true };
  } catch (error) {
    console.error('❌ Error logging WhatsApp message:', error);
    return { success: false, error: error.message };
  }
};

/*
 * getOrCreateUser() lived here. Removed during the security review:
 * it was never called, and it wrote unauthenticated rows into
 * public.users — the table holding customer PII, which is now
 * restricted to "your own row" by RLS. Profiles are created by the
 * on_auth_user_created trigger on signup instead.
 *
 * The birth-number helpers it used now live in src/utils/astrology.js.
 */

/**
 * Track analytics event
 */
export const trackEvent = async (eventData) => {
  try {
    const {
      eventType,
      productId = null,
      pageUrl = window.location.href,
      deviceType = /Mobile/.test(navigator.userAgent) ? 'mobile' : 'desktop',
    } = eventData;

    const { error } = await supabase
      .from('analytics_events')
      .insert({
        event_type: eventType,
        product_id: productId,
        page_url: pageUrl,
        device_type: deviceType,
        browser: navigator.userAgent,
      });

    if (error) throw error;
  } catch (error) {
    console.error('❌ Error tracking event:', error);
  }
};

/**
 * Get products from Supabase
 */
export const getProducts = async (filters = {}) => {
  try {
    let query = supabase
      .from('products')
      .select('*')
      .eq('is_active', true);

    if (filters.category && filters.category !== 'all') {
      query = query.eq('category', filters.category);
    }

    if (filters.planet && filters.planet !== 'all') {
      query = query.eq('planet', filters.planet);
    }

    if (filters.inStock) {
      query = query.eq('in_stock', true);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, products: data };
  } catch (error) {
    console.error('❌ Error fetching products:', error);
    return { success: false, error: error.message, products: [] };
  }
};